import { Alert, Form, Input, Modal, Select } from "antd";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type {
  LeadCreate,
  Operator,
} from "@/providers/smarta-accounts/accounts-api-client";
import { CompanyType } from "@/v1/types/IUser.type";
import { createLead, LeadEmailTakenError } from "./api";
import { matchesOperatorName } from "./operatorSearch";

/*
 * The modal body scrolls inside .ant-modal-wrap when the viewport is short. A
 * dropdown attached to <body> (antd's default) stays where it opened while the
 * form scrolls under it, so it drifts off its field and over other fields.
 * Rendering it next to its select, inside the dialog, keeps it attached. Same
 * approach as AntDeletePopupConfirm, the one existing getPopupContainer here.
 */
const popupInsideDialog = (trigger: HTMLElement) =>
  trigger.parentElement ?? document.body;

interface NewLeadFormValues {
  displayName: string;
  email: string;
  phone?: string;
  legalForm: CompanyType;
  assignedOperatorId?: string;
}

/*
 * Labelled with the enum values themselves, as the customer forms do
 * (CreateUserForm, BusinessInformation). They are the German legal form names.
 */
const LEGAL_FORM_OPTIONS = Object.values(CompanyType).map((value) => ({
  value,
  label: value,
}));

/*
 * Send only what was entered: an empty optional field is left out, never sent
 * as an empty string.
 */
const toPayload = (values: NewLeadFormValues): LeadCreate => {
  const phone = values.phone?.trim();
  return {
    displayName: values.displayName.trim(),
    email: values.email.trim(),
    ...(phone ? { phone } : {}),
    legalForm: values.legalForm,
    ...(values.assignedOperatorId
      ? { assignedOperatorId: values.assignedOperatorId }
      : {}),
  };
};

interface Props {
  open: boolean;
  /** Loaded once by the page and shared with the table. */
  operators: Operator[];
  operatorsError: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const NewLeadModal = ({
  open,
  operators,
  operatorsError,
  onClose,
  onCreated,
}: Props) => {
  const { t } = useTranslation("Onboarding");
  const [form] = Form.useForm<NewLeadFormValues>();
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState(false);
  // The button's loading state lags one render; the ref closes that gap.
  const savingRef = useRef(false);

  const close = () => {
    // Closing mid-request would reset the form, and the answer (a 409 or any
    // other error) would then land in a hidden, empty form.
    if (savingRef.current) return;
    form.resetFields();
    setFormError(false);
    onClose();
  };

  const submit = async (values: NewLeadFormValues) => {
    if (savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setFormError(false);
    try {
      await createLead(toPayload(values));
      form.resetFields();
      onCreated();
    } catch (error) {
      if (error instanceof LeadEmailTakenError) {
        form.setFields([
          { name: "email", errors: [String(t("newLead.errors.emailTaken"))] },
        ]);
      } else {
        setFormError(true);
      }
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t("newLead.title")}
      okText={t("newLead.save")}
      cancelText={t("newLead.cancel")}
      okButtonProps={{ loading: saving }}
      cancelButtonProps={{ disabled: saving }}
      closable={!saving}
      keyboard={!saving}
      maskClosable={false}
      onOk={() => form.submit()}
      onCancel={close}
    >
      {formError && (
        <Alert
          type="error"
          showIcon
          message={t("newLead.errors.generic")}
          style={{ marginBottom: 16 }}
        />
      )}
      <Form<NewLeadFormValues>
        form={form}
        layout="vertical"
        name="new-lead"
        onFinish={submit}
      >
        <Form.Item
          name="displayName"
          label={t("newLead.fields.displayName")}
          rules={[
            {
              required: true,
              whitespace: true,
              message: String(t("newLead.errors.displayNameRequired")),
            },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="email"
          label={t("newLead.fields.email")}
          rules={[
            {
              required: true,
              message: String(t("newLead.errors.emailRequired")),
            },
            {
              type: "email",
              message: String(t("newLead.errors.emailInvalid")),
            },
          ]}
        >
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item name="phone" label={t("newLead.fields.phone")}>
          <Input autoComplete="off" />
        </Form.Item>
        <Form.Item
          name="legalForm"
          label={t("newLead.fields.legalForm")}
          rules={[
            {
              required: true,
              message: String(t("newLead.errors.legalFormRequired")),
            },
          ]}
        >
          <Select
            options={LEGAL_FORM_OPTIONS}
            getPopupContainer={popupInsideDialog}
          />
        </Form.Item>
        <Form.Item
          name="assignedOperatorId"
          label={t("newLead.fields.assignee")}
        >
          <Select
            allowClear
            showSearch
            filterOption={matchesOperatorName}
            getPopupContainer={popupInsideDialog}
            placeholder={t("newLead.unassigned")}
            notFoundContent={
              operatorsError ? t("newLead.errors.operators") : undefined
            }
            options={operators.map((operator) => ({
              value: operator.id,
              label: operator.displayName,
            }))}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};
